/**
 * Toast Notification System
 *
 * Provides user feedback for success, error, and undo actions.
 * Accessible with ARIA live regions.
 */

import { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { CheckCircle, XCircle, AlertCircle, X, Undo2 } from 'lucide-react';

type ToastType = 'success' | 'error' | 'info';

interface Toast {
  id: string;
  type: ToastType;
  message: string;
  undoAction?: () => void;
  duration?: number;
}

interface ToastContextValue {
  showToast: (toast: Omit<Toast, 'id'>) => void;
  showSuccess: (message: string) => void;
  showError: (message: string) => void;
  showUndo: (message: string, undoAction: () => void, duration?: number) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

export function useToast() {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
}

const ICONS = {
  success: CheckCircle,
  error: XCircle,
  info: AlertCircle,
} as const;

const ICON_COLORS = {
  success: 'text-category-health',
  error: 'text-error',
  info: 'text-primary',
} as const;

const BG_COLORS = {
  success: 'bg-category-health/10 border-category-health/30',
  error: 'bg-error/10 border-error/30',
  info: 'bg-primary/10 border-primary/30',
} as const;

function ToastItem({
  toast,
  onDismiss,
}: {
  toast: Toast;
  onDismiss: (id: string) => void;
}) {
  const [isExiting, setIsExiting] = useState(false);
  const [progress, setProgress] = useState(100);
  const duration = toast.duration ?? 4000;

  const handleDismiss = useCallback(() => {
    setIsExiting(true);
    setTimeout(() => onDismiss(toast.id), 200);
  }, [onDismiss, toast.id]);

  const handleUndo = useCallback(() => {
    toast.undoAction?.();
    handleDismiss();
  }, [toast.undoAction, handleDismiss]);

  useEffect(() => {
    const startTime = Date.now();
    const interval = setInterval(() => {
      const elapsed = Date.now() - startTime;
      const remaining = Math.max(0, 100 - (elapsed / duration) * 100);
      setProgress(remaining);
      if (remaining === 0) {
        clearInterval(interval);
        handleDismiss();
      }
    }, 50);

    return () => clearInterval(interval);
  }, [duration, handleDismiss]);

  const Icon = ICONS[toast.type];

  return (
    <div
      role="alert"
      aria-live="polite"
      className={`
        relative flex items-center gap-3 px-4 py-3 rounded-lg border shadow-warm-md
        transform transition-all duration-200
        ${BG_COLORS[toast.type]}
        ${isExiting ? 'opacity-0 translate-x-4' : 'opacity-100 translate-x-0'}
      `}
    >
      <Icon className={`h-5 w-5 shrink-0 ${ICON_COLORS[toast.type]}`} aria-hidden="true" />
      <p className="flex-1 text-sm text-text-primary">{toast.message}</p>

      {toast.undoAction && (
        <button
          type="button"
          onClick={handleUndo}
          className="
            flex items-center gap-1 px-2 py-1 rounded-md
            text-sm font-medium text-primary
            hover:bg-primary/10 transition-colors
            focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary
          "
        >
          <Undo2 className="h-4 w-4" aria-hidden="true" />
          Undo
        </button>
      )}

      <button
        type="button"
        onClick={handleDismiss}
        aria-label="Dismiss notification"
        className="
          p-1 rounded-md text-text-tertiary
          hover:text-text-primary hover:bg-bg-tertiary
          transition-colors
          focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary
        "
      >
        <X className="h-4 w-4" />
      </button>

      {/* Progress bar for undo toasts */}
      {toast.undoAction && (
        <div className="absolute bottom-0 left-0 right-0 h-1 bg-border-light rounded-b-lg overflow-hidden">
          <div
            className="h-full bg-primary transition-all duration-50 ease-linear"
            style={{ width: `${progress}%` }}
          />
        </div>
      )}
    </div>
  );
}

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const showToast = useCallback((toast: Omit<Toast, 'id'>) => {
    const id = `toast-${Date.now()}-${Math.random().toString(36).slice(2)}`;
    setToasts((prev) => [...prev, { ...toast, id }]);
  }, []);

  const showSuccess = useCallback(
    (message: string) => {
      showToast({ type: 'success', message });
    },
    [showToast]
  );

  const showError = useCallback(
    (message: string) => {
      showToast({ type: 'error', message, duration: 6000 });
    },
    [showToast]
  );

  const showUndo = useCallback(
    (message: string, undoAction: () => void, duration = 5000) => {
      showToast({ type: 'info', message, undoAction, duration });
    },
    [showToast]
  );

  const dismissToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  return (
    <ToastContext.Provider value={{ showToast, showSuccess, showError, showUndo }}>
      {children}

      {/* Toast container */}
      <div
        aria-label="Notifications"
        className="fixed bottom-4 right-4 z-50 flex flex-col gap-2 max-w-sm w-full pointer-events-none"
      >
        {toasts.map((toast) => (
          <div key={toast.id} className="pointer-events-auto">
            <ToastItem toast={toast} onDismiss={dismissToast} />
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}
